/**
 * Client-side helpers for the mail search query language.
 *
 * The authoritative parser lives on the server (`backend/utils/mailQuery.js`);
 * this module only needs to *write* queries (from the advanced-search form) and
 * read them back well enough to repopulate that form, so the user can open
 * search options on an existing query without losing what they typed.
 */

/** Attachment kinds offered by `has:`. */
export const ATTACHMENT_KINDS = [
  { value: '', label: 'Any attachment' },
  { value: 'pdf', label: 'PDF' },
  { value: 'image', label: 'Image' },
  { value: 'document', label: 'Document' },
  { value: 'spreadsheet', label: 'Spreadsheet' },
  { value: 'presentation', label: 'Presentation' },
];

/** Folders offered by `in:`. */
export const SEARCH_SCOPES = [
  { value: '', label: 'All mail' },
  { value: 'inbox', label: 'Inbox' },
  { value: 'sent', label: 'Sent' },
  { value: 'draft', label: 'Drafts' },
  { value: 'archive', label: 'Archive' },
  { value: 'spam', label: 'Spam' },
  { value: 'trash', label: 'Trash' },
  { value: 'anywhere', label: 'Mail & spam & trash' },
];

export const SEARCH_CATEGORIES = [
  { value: '', label: 'Any category' },
  { value: 'primary', label: 'Primary' },
  { value: 'social', label: 'Social' },
  { value: 'promotions', label: 'Promotions' },
  { value: 'updates', label: 'Updates' },
  { value: 'forums', label: 'Forums' },
];

/** Relative age offered by `older_than:`. */
export const DATE_RANGES = [
  { value: '', label: 'Any time' },
  { value: '1d', label: 'Older than 1 day' },
  { value: '3d', label: 'Older than 3 days' },
  { value: '1w', label: 'Older than 1 week' },
  { value: '1m', label: 'Older than 1 month' },
  { value: '6m', label: 'Older than 6 months' },
  { value: '1y', label: 'Older than 1 year' },
];

/** Reference shown in the syntax hint. */
export const OPERATOR_HELP = [
  { syntax: 'from:alice', description: 'Sender contains' },
  { syntax: 'to:bob@x.com', description: 'Recipient contains' },
  { syntax: 'cc: / bcc:', description: 'Copied recipients' },
  { syntax: 'subject:"weekly report"', description: 'Subject contains a phrase' },
  { syntax: 'label:Finance', description: 'Carries a label' },
  { syntax: 'has:attachment', description: 'Also image, pdf, document, spreadsheet, presentation' },
  { syntax: 'filename:invoice.pdf', description: 'Attachment name contains' },
  { syntax: 'is:unread', description: 'Also read, starred, important, snoozed, scheduled' },
  { syntax: 'in:archive', description: 'Also inbox, sent, draft, spam, trash, anywhere' },
  { syntax: 'category:promotions', description: 'Inbox category' },
  { syntax: 'after:2026/01/31', description: 'Also before:' },
  { syntax: 'older_than:2w', description: 'Also newer_than: — h, d, w, m, y' },
  { syntax: 'larger:5M', description: 'Also smaller: — k, M, G' },
  { syntax: '-promotions', description: 'Exclude a term with a leading dash' },
  { syntax: 'from:alice OR from:bob', description: 'Match either side' },
];

/** Blank advanced-search form state. */
export const EMPTY_FIELDS = Object.freeze({
  from: '',
  to: '',
  subject: '',
  hasWords: '',
  notWords: '',
  filename: '',
  label: '',
  scope: '',
  category: '',
  attachment: false,
  attachmentKind: '',
  after: '',
  before: '',
  olderThan: '',
  largerThan: '',
  smallerThan: '',
  isUnread: false,
  isStarred: false,
  isImportant: false,
});

/** Quote an operand when it contains whitespace, so it stays one clause. */
const quote = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return /\s/.test(trimmed) ? `"${trimmed}"` : trimmed;
};

/**
 * Turn advanced-search form state into a query string.
 *
 * Free-text words are appended last so the query reads the way a person would
 * type it, and empty fields contribute nothing.
 */
export const buildQuery = (fields = {}) => {
  const parts = [];
  const operand = (operator, value) => {
    const quoted = quote(value);
    if (quoted) parts.push(`${operator}:${quoted}`);
  };

  if (fields.scope) parts.push(`in:${fields.scope}`);
  operand('from', fields.from);
  operand('to', fields.to);
  operand('subject', fields.subject);
  operand('label', fields.label);
  operand('filename', fields.filename);
  if (fields.category) parts.push(`category:${fields.category}`);

  if (fields.attachmentKind) parts.push(`has:${fields.attachmentKind}`);
  else if (fields.attachment) parts.push('has:attachment');

  if (fields.isUnread) parts.push('is:unread');
  if (fields.isStarred) parts.push('is:starred');
  if (fields.isImportant) parts.push('is:important');

  if (fields.after) parts.push(`after:${fields.after}`);
  if (fields.before) parts.push(`before:${fields.before}`);
  if (fields.olderThan) parts.push(`older_than:${fields.olderThan}`);
  if (fields.largerThan) parts.push(`larger:${fields.largerThan}`);
  if (fields.smallerThan) parts.push(`smaller:${fields.smallerThan}`);

  const words = String(fields.hasWords || '').trim();
  if (words) parts.push(words);

  String(fields.notWords || '')
    .split(/\s+/)
    .filter(Boolean)
    .forEach((word) => parts.push(`-${quote(word)}`));

  return parts.join(' ');
};

const OPERAND_FIELDS = {
  from: 'from',
  to: 'to',
  subject: 'subject',
  label: 'label',
  filename: 'filename',
};

/**
 * Read a query string back into form state — best effort, mirroring
 * `buildQuery`. Anything not recognised lands in `hasWords` so it survives a
 * round trip through the form instead of being silently dropped.
 */
export const parseQueryFields = (query) => {
  const fields = { ...EMPTY_FIELDS };
  const words = [];
  const excluded = [];

  // Same shape as the server tokenizer: quoted runs stay together.
  const tokens = String(query || '').match(/-?(?:[a-z_]+:)?(?:"[^"]*"|[^\s"]+)/gi) || [];

  tokens.forEach((token) => {
    let text = token;
    let negated = false;
    if (text.startsWith('-') && text.length > 1) {
      negated = true;
      text = text.slice(1);
    }

    const separator = text.indexOf(':');
    const operator = separator > 0 ? text.slice(0, separator).toLowerCase() : '';
    const value = separator > 0 ? text.slice(separator + 1).replace(/^"|"$/g, '') : '';

    if (!negated && OPERAND_FIELDS[operator]) {
      fields[OPERAND_FIELDS[operator]] = value;
      return;
    }
    if (!negated && operator === 'in') {
      fields.scope = value.toLowerCase();
      return;
    }
    if (!negated && operator === 'category') {
      fields.category = value.toLowerCase();
      return;
    }
    if (!negated && operator === 'has') {
      if (value.toLowerCase() === 'attachment') fields.attachment = true;
      else fields.attachmentKind = value.toLowerCase();
      return;
    }
    if (!negated && operator === 'is') {
      if (value.toLowerCase() === 'unread') fields.isUnread = true;
      else if (value.toLowerCase() === 'starred') fields.isStarred = true;
      else if (value.toLowerCase() === 'important') fields.isImportant = true;
      return;
    }
    if (!negated && (operator === 'after' || operator === 'before')) {
      fields[operator] = value;
      return;
    }
    if (!negated && operator === 'older_than') {
      fields.olderThan = value;
      return;
    }
    if (!negated && operator === 'larger') {
      fields.largerThan = value;
      return;
    }
    if (!negated && operator === 'smaller') {
      fields.smallerThan = value;
      return;
    }

    const plain = text.replace(/^"|"$/g, '');
    if (negated) excluded.push(plain);
    else words.push(plain);
  });

  fields.hasWords = words.join(' ');
  fields.notWords = excluded.join(' ');
  return fields;
};

/** True when any field would contribute to a query. */
export const hasAnyField = (fields = {}) => buildQuery(fields).trim().length > 0;
